/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { Alert, Button, Typography } from '@mui/material';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { WarningIcon } from '../../../../components/svg-icons/WarningIcon';
import { BasicRoutes } from '../../../../constants/basicRoutes';
import { Namespaces } from '../../../../i18n';
import { UserApiService } from '../../../../services/UserApiService';
import { userLogout } from '../../../../store/auth/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { DataState } from '../../../../types/common/DataState';
import { dataLoaded, dataLoading, isDataLoading } from '../../../../utils/dataState';
import { useObjectState } from '../../../../utils/useObjectState';
import { useDeleteAccountStyles } from './DeleteAccount.styles';

type State = {
  isOpenConfirmModal: boolean;
  isOpenErrorModal: boolean;
  errorMessage?: string;
  dataState: DataState;
};

export function DeleteAccount() {
  const classes = useDeleteAccountStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation([Namespaces.UserProfile, Namespaces.Common]);
  const [state, setState] = useObjectState<State>({
    isOpenConfirmModal: false,
    isOpenErrorModal: false,
    dataState: DataState.loaded,
  });
  const { isOpenErrorModal, isOpenConfirmModal, dataState, errorMessage } = state;
  const deleteUser = async () => {
    try {
      setState({
        errorMessage: undefined,
        isOpenErrorModal: false,
        isOpenConfirmModal: false,
        dataState: dataLoading(),
      });
      await UserApiService.deleteUser();
      void navigate(BasicRoutes.HOME);
      dispatch(userLogout());
    } catch (e) {
      const error = e as ApiClientError;
      const responseData = error.response?.data;
      if (responseData) {
        setState({
          isOpenErrorModal: true,
          errorMessage: responseData.message,
        });
      }
      setState({ dataState: dataLoaded() });
    }
  };
  const onDeleteUserClick = () => {
    void deleteUser();
  };
  return (
    <>
      <HUSpin spinning={isDataLoading(dataState)}>
        <Typography variant="h4" className={classes.title}>
          {t('delete_account.title')}
        </Typography>
        <Alert className={classes.warning} icon={<WarningIcon />} severity="warning">
          {t('delete_account.warning')}
        </Alert>
        <Button
          type="submit"
          variant="contained"
          disabled={isDataLoading(dataState)}
          size="large"
          onClick={() => setState({ isOpenConfirmModal: true })}
        >
          {t('delete_account.button_text')}
        </Button>
      </HUSpin>
      <HUModal2
        open={isOpenConfirmModal}
        size="big"
        okButtonText={t(`${Namespaces.Common}:confirmation.yes`)}
        onOk={onDeleteUserClick}
        cancelButtonText={t(`${Namespaces.Common}:confirmation.no`)}
        onCancel={() => setState({ isOpenConfirmModal: false })}
      >
        <Typography variant="body1" classes={{ root: classes.modalText }}>
          {t(`${Namespaces.Common}:confirmation.delete_account`)}
        </Typography>
      </HUModal2>
      <HUModal2
        open={isOpenErrorModal}
        size="big"
        okButtonText={t(`${Namespaces.Common}:label.ok`)}
        onOk={() => setState({ isOpenErrorModal: false })}
      >
        <Typography variant="body1" classes={{ root: classes.modalText }}>
          {t([
            `${Namespaces.Common}:delete_account_errors.${errorMessage}`,
            `${Namespaces.Common}:errors.unexpected_error`,
          ])}
        </Typography>
      </HUModal2>
    </>
  );
}
